import React, { useRef, useEffect, useState } from "react";
import FooterPanel from "../common/FooterPanel";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css"; // Import styles for phone input
import MenuBar from "../common/MenuPanel";
import { getGroups, saveGroup } from "../../services/RetreatServices";
import { getAllTransactions } from "../../services/PayPalServices";
import { useNavigate, useLocation } from "react-router-dom";

import EventSupport from "./EventSupport"; // Import the EventSupport class
import { sendEmail } from "../../services/EmailSender";
import UtilMethods from "../../util/UtilMethods";
import AlertModal from "../common/AlertModal";
import { useAuth } from "../../auth/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { saveAs } from "file-saver";
// Constants for dropdown options
import { STATUS_OPTIONS } from "../common/Constants";
import * as XLSX from "xlsx";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

const AdminRetreatRegistrationPage = () => {
  const eventsPath =
    process.env.REACT_APP_EVENTS_SOURCE || "source/events.json";
  const events = require(`../../${eventsPath}`);
  const fileInputRef = useRef(); // Create a reference for the file input
  const [groups, setGroups] = useState([]);
  const { user, userType } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [editingGroup, setEditingGroup] = useState(null);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [filters, setFilters] = useState({
    email: "",
    phone: "",
    retreatID: "",
    registrationIDs: "",
    status: "",
    refundAmount: "",
    name: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;

  const filteredGroups = groups.filter((group) => {
    return (
      (filters.email ? group.email.includes(filters.email) : true) &&
      (filters.retreatID
        ? group.retreatID.includes(filters.retreatID)
        : true) &&
      (filters.name
        ? group.firstName.toLowerCase().includes(filters.name.toLowerCase()) ||
          group.lastName.toLowerCase().includes(filters.name.toLowerCase())
        : true)
    );
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredGroups.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const totalPages = Math.ceil(filteredGroups.length / recordsPerPage);

  // Set retreatID based on active retreat
  useEffect(() => {
    //console.log("useEffect 1 1 ", filters);
    const activeRetreat = events.find((event) =>
      UtilMethods.canRegister(
        event.reg_date,
        event.reg_end_date ? event.reg_end_date : event.start_date
      )
    );
    if (activeRetreat) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        retreatID: activeRetreat.id.toString(),
      }));
    }
    //UtilMethods.log("useEffect 1 2 ", filters);
  }, []); // Only runs once on mount

  // Fetch data when location state changes (filters)
  useEffect(() => {
    console.log("useEffect 2 1 ", filters);
    const fetchData = async () => {
      setLoading(true); // Set loading true before starting fetch
      UtilMethods.log("location.state?.filters", location.state?.filters);

      // Handle filters from location state, if provided
      try {
        if (location.state?.filters) {
          setFilters(location.state.filters);
          UtilMethods.log("useEffect 2 2 ", filters);
          const response = await getGroups(location.state.filters);
          setGroups(response);
        }
      } catch (err) {
        setError("Failed to fetch groups: " + err);
      } finally {
        setLoading(false); // Set loading to false after fetch completion
      }
    };
    console.log("useEffect 2 3 ", filters);

    fetchData(); // Trigger fetching data
  }, [location]); // Runs when location changes

  const fetchGroups = async () => {
    setLoading(true); // Ensure loading state is set to true at the beginning
    try {
      console.log("fetchGroups filters", filters);
      const response = await getGroups(filters);
      setGroups(response);
    } catch (err) {
      setError("Failed to fetch groups: " + err);
    }
    setLoading(false);
  };

  const toggleSummary = () => {
    setIsSummaryExpanded(!isSummaryExpanded);
  };

  const handleEditClick = (group) => {
    if (group) {
      const eventId = group.retreatID;
      group.updatedBy = user?.userID;
      console.log("handleEditClick eventId:", eventId);
      navigate(`/retreat-registration/${eventId}`, {
        state: { group, filters },
      });
    }
  };
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  const registerNewClick = async () => {
    console.log("registerNewClick filters.retreatID:", filters.retreatID);
    if (!filters.retreatID) {
      setError("Please select a Retreat ID before registering a new group.");
      setShowErrorModal(true);
      return;
    }

    const eventId = filters.retreatID;
    console.log("registerNewClick eventId:", eventId);
    navigate(`/retreat-registration/${eventId}`, {
      state: { mode: "ADMIN", filters },
    });
  };

  const handleFileSelection = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadAdminData(file); // Pass the file to uploadAdminData
    }
  };

  const downloadTemplate = async () => {
    console.log("downloadTemplate filters.retreatID:", filters.retreatID);

    if (!filters.retreatID) {
      setError("Please select a Retreat ID before registering a new group.");
      setShowErrorModal(true);
      return;
    }

    let data = [];
    try {
      console.log("fetchGroups filters", filters);
      const response = await getGroups(filters);
      data = response || [];
    } catch (err) {
      setError("Failed to fetch groups: " + err.message);
      setShowErrorModal(true);
      return;
    }

    const templateData = [
      [
        "Registration ID",
        "Full Name",
        "Email",
        "Phone",
        "Count",
        "Group Members",
        "Status",
        "Payment Reference",
        "Payment Method",
        "Amount",
        "Paid Amount",
        "Payment Remarks",
        "Refund Amount",
        "Refund Status",
        "Remarks",
        "Updated Date",
      ],
    ];

    data.forEach((group) => {
      const paymentReference = [
        group.retreatID ? `ID:${group.retreatID}` : "",
        group.registrationID ? `-${group.registrationID}` : "",
        group.firstName && group.lastName
          ? `, Name: ${group.firstName} ${group.lastName}`
          : "",
        group.phone ? `, Phone: ${group.phone}` : "",
      ].join("");

      templateData.push([
        group.registrationID || "",
        `${group.firstName || ""} ${group.lastName || ""}`.trim(),
        group.email || "",
        group.phone || "",
        group.numberOfParticipants,
        UtilMethods.formatGroupMembersforXLS(group.groupMembers),
        group.status || "",
        paymentReference,
        group.paymentMethod || "",
        group.amount || 0,
        group.paidAmount || 0,
        group.paymentInfo || "",
        group.refundAmount || 0,
        group.refundStatus || "",
        group.remarks || "",
        group.updatedAt ? new Date(group.updatedAt).toLocaleString() : "",
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    const binaryWorkbook = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "binary",
    });

    const blob = new Blob(
      [
        new Uint8Array(
          binaryWorkbook.split("").map((char) => char.charCodeAt(0))
        ),
      ],
      { type: "application/octet-stream" }
    );

    const fileName = "Retreat_Payments_ID_" + filters.retreatID + ".xlsx";

    saveAs(blob, fileName || "PaymentUploadTemplate.xlsx");
  };

  const triggerFileUpload = () => {
    console.log("uploadAdminData filters.retreatID:", filters.retreatID);

    if (!filters.retreatID) {
      setError("Please select a Retreat ID before registering a new group.");
      setShowErrorModal(true);
      return;
    }

    fileInputRef.current.click();
  };

  const uploadAdminData = async (file) => {
    console.log("Uploading file:", file);
    setLoading(true);
    // Ensure the file is provided and is an Excel file
    if (!file) {
      setLoading(false);
      setError("No file selected. Please upload a valid Excel file.");
      setShowErrorModal(true);

      return;
    }

    const fileType = file.name.split(".").pop().toLowerCase();
    if (fileType !== "xlsx" && fileType !== "xls") {
      setLoading(false);
      setError("Invalid file type. Please upload an Excel file.");
      setShowErrorModal(true);
      return;
    }

    try {
      const data = await file.arrayBuffer(); // Read file as ArrayBuffer
      const workbook = XLSX.read(data, { type: "array" }); // Parse the data
      const sheetName = workbook.SheetNames[0]; // Get the first sheet name
      const worksheet = workbook.Sheets[sheetName]; // Get the worksheet
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Convert to JSON
      // Arrays to store success and error messages
      const successMessages = [];
      const errorMessages = [];
      // Validate and process the data
      const processedData = jsonData
        .map((row, index) => {
          const {
            "Full Name": fullName,
            Amount: amount,
            "Paid Amount": paidAmount,
            Status: status,
            "Refund Amount": refundAmount,
            "Registration ID": registrationID,
            Email,
            Phone,
            "Payment Remarks": paymentInfo,
            "Payment Method": paymentMethod,
            "Special Request": specialRequest,
            "Group Members": groupMembers,
          } = row;

          if (!registrationID || !Email || !Phone) {
            console.warn(`Row ${index + 1} is missing required fields.`);
            errorMessages.push(
              `Row ${index + 1} is missing required fields.` + "\n"
            );
            return null;
          }
          if (status !== "Approved" && status !== "Confirmed") {
            console.warn(
              `Row ${
                index + 1
              } Status is not Approved or Confirmed; ignoring the record`
            );
            errorMessages.push(
              `Row ${
                index + 1
              } Status is not Approved or Confirmed; ignoring the record` + "\n"
            );
            return null;
          }

          return {
            retreatID: filters.retreatID,
            fullName,
            amount,
            paymentMethod,
            paymentInfo,
            specialRequest,
            paidAmount,
            status,
            refundAmount,
            registrationID,
            email: Email,
            phone: Phone,
            updatedBy: user?.userID,
            approvedOn: new Date().toISOString(),
            groupMembers,
          };
        })
        .filter(Boolean); // Remove null rows

      console.log("Processed Data:", processedData);

      // Iterate and call updateGroupStatus for each record
      for (const groupInfo of processedData) {
        try {
          console.log("Processing group:", groupInfo);
          const response = await EventSupport.updateGroupStatus(
            groupInfo,
            "Confirmed"
          ); // Await the response from update Group

          if (response) {
            const eventData = await EventSupport.fetchEventData(
              groupInfo.retreatID
            );
            const grpForEmail = response?.group ? response.group : groupInfo;
            // ensure group members are populated (try response.group first, then fall back to local `groups` state)
            if (
              !grpForEmail.groupMembers ||
              grpForEmail.groupMembers.length === 0
            ) {
              const existing = groups.find(
                (g) =>
                  g.registrationID === grpForEmail.registrationID &&
                  String(g.retreatID) === String(grpForEmail.retreatID)
              );
              grpForEmail.groupMembers = existing?.groupMembers || [];
            }
            console.log(
              "Group updated successfully grpForEmail :",
              grpForEmail
            );
            console.log(
              "Group updated successfully groupMembers :",
              grpForEmail.groupMembers
            );
            const emailData = {
              fullName: grpForEmail.firstName + " " + grpForEmail.lastName,
              email: grpForEmail.email,
              phone: grpForEmail.phone,
              amount: grpForEmail.amount,
              paymentMethod: grpForEmail.paymentMethod,
              status: grpForEmail.status,
              groupMembers:
                UtilMethods.formatGroupMembers(grpForEmail.groupMembers) + "",
              registrationID: grpForEmail.registrationID,
              retreatName: `${eventData.name}`,
              paidAmount: grpForEmail.paidAmount,
              whatsAppLink: eventData.whatsAppLink,
              retreatEmail: eventData.retreatEmail,
            };

            console.log("Email Data:", emailData);
            const emailResponse = await sendEmail(
              emailData,
              "retreatApproval",
              "service_retreat"
            );

            if (emailResponse.success) {
              successMessages.push(
                `Registration ID: ${
                  response?.group?.registrationID || "NA"
                } - Email confirmation sent to: ${groupInfo.email}`
              );
            } else {
              errorMessages.push(
                `Failed to send email for Registration ID: ${
                  response?.group?.registrationID || "NA"
                }`
              );
            }
          } else {
            errorMessages.push(
              `Failed to approve group with Registration ID: ${groupInfo.registrationID}`
            );
          }
        } catch (error) {
          console.error(
            `Failed to update group status for Registration ID: ${groupInfo.registrationID}`,
            error
          );
          errorMessages.push(
            `Error updating group status for Registration ID: ${groupInfo.registrationID}`
          );
        }
      }

      // After processing all groups, show the modal with consolidated messages
      const combinedMessages = [...successMessages, ...errorMessages];
      if (combinedMessages.length > 0) {
        if (successMessages.length > 0) {
          setSuccessMessage(combinedMessages);
          setShowSuccessModal(true);
        } else {
          setError(combinedMessages);
          setShowErrorModal(true);
        }
      }
      // Fetch updated groups after processing
      fetchGroups();
    } catch (error) {
      console.error("Error uploading admin data:", error);
      setError(
        "Failed to process the Excel file. Please check the format and try again."
      );
      setShowErrorModal(true);
    }
    setLoading(false);
  };
  const sendWaiverEmail = async (
    groupInfo,
    eventData,
    successMessages,
    errorMessages,
    member
  ) => {
    const linkW = member
      ? `${process.env.REACT_APP_URL}/waiver-form?registrationID=${groupInfo.registrationID}&retreatID=${groupInfo.retreatID}&memberID=${member.memberID}`
      : `${process.env.REACT_APP_URL}/waiver-form?registrationID=${groupInfo.registrationID}&retreatID=${groupInfo.retreatID}`;
    const emailData = {
      fullName: member
        ? `${member.firstName} ${member.lastName}`
        : `${groupInfo.firstName} ${groupInfo.lastName}`,
      email:
        member && member.age >= 18 && member.email
          ? member.email
          : groupInfo.email,
      phone: member ? member.phone : groupInfo.phone,
      amount: groupInfo.amount,
      paymentMethod: groupInfo.paymentMethod,
      status: groupInfo.status,
      groupMembers: member
        ? UtilMethods.formatGroupMembers([member])
        : `${UtilMethods.formatGroupMembers(groupInfo.groupMembers)}`,
      registrationID: groupInfo.registrationID,
      retreatName: `${eventData.name}`,
      paidAmount: eventData.paidAmount,
      waiverLink: linkW,
      whatsAppLink: eventData.whatsAppLink,
      retreatEmail: eventData.retreatEmail,
    };

    console.log("Email Data:", emailData.fullName);
    console.log("Email linkW:", emailData.linkW);
    const emailResponse = await sendEmail(
      emailData,
      "TMP_Waiver",
      "service_retreat"
    );
    if (emailResponse.success) {
      successMessages.push(
        `Registration ID: ${
          groupInfo.registrationID || "NA"
        }, Waiver Form Email sent to: ${
          member && member.age >= 18 && member.email
            ? member.email
            : groupInfo.email
        }` + "\n"
      );
    } else {
      errorMessages.push(
        `Failed to send email. Registration ID: ${
          groupInfo.registrationID || "NA"
        }, Email: ${
          member && member.age >= 18 && member.email
            ? member.email
            : groupInfo.email
        }` + "\n"
      );
    }
  };
  const sendWaiverFormEmail = async (groupInfo, member, mode) => {
    console.log("sendEmail groupInfo.retreatID:", groupInfo?.retreatID);
    console.log("sendWaiverFormEmail groups:", groups?.length);
    if (!groupInfo && groups.length === 0) {
      setError("Error: No groups found for the selected Retreat ID.");
      setShowErrorModal(true);
      return; // Exit the function if there are no groups
    }
    setLoading(true);
    // Arrays to store success and error messages
    const successMessages = [];
    const errorMessages = [];

    if (mode === "MEMBER" && member) {
      const eventData = await EventSupport.fetchEventData(groupInfo?.retreatID);
      await sendWaiverEmail(
        groupInfo,
        eventData,
        successMessages,
        errorMessages,
        member
      );
    } else if (mode === "GROUP" && groupInfo) {
      const eventData = await EventSupport.fetchEventData(groupInfo?.retreatID);
      try {
        await sendWaiverEmail(
          groupInfo,
          eventData,
          successMessages,
          errorMessages,
          null
        );
      } catch (error) {
        console.error(
          `Failed to send email for Registration ID: ${groupInfo.registrationID}`,
          error
        );
        errorMessages.push(
          `An error occurred for Registration ID: ${
            groupInfo.registrationID || "NA" + "\n"
          }`
        );
      }
    } else if (mode === "MEMBER" && groupInfo) {
      const eventData = await EventSupport.fetchEventData(groupInfo?.retreatID);
      for (const member of groupInfo.groupMembers.filter(
        (member) => !member.deleted
      )) {
        try {
          await sendWaiverEmail(
            groupInfo,
            eventData,
            successMessages,
            errorMessages,
            member
          );
        } catch (error) {
          console.error(
            `Failed to send email for Registration ID: ${groupInfo.registrationID}`,
            error
          );
          errorMessages.push(
            `An error occurred for Registration ID: ${
              groupInfo.registrationID || "NA" + "\n"
            }`
          );
        }
      }
    } else if (mode === "GROUPS") {
      for (const groupInfo of groups) {
        try {
          const eventData = await EventSupport.fetchEventData(
            groupInfo?.retreatID
          );
          await sendWaiverEmail(
            groupInfo,
            eventData,
            successMessages,
            errorMessages,
            null
          );
        } catch (error) {
          console.error(
            `Failed to send email for Registration ID: ${groupInfo.registrationID}`,
            error
          );
          errorMessages.push(
            `An error occurred for Registration ID: ${
              groupInfo.registrationID || "NA" + "\n"
            }`
          );
        }
      }
    } else if (mode === "GROUPS-MEMBERS") {
      for (const groupInfo of groups) {
        const eventData = await EventSupport.fetchEventData(
          groupInfo?.retreatID
        );
        for (const member of groupInfo.groupMembers.filter(
          (member) => !member.deleted
        )) {
          try {
            await sendWaiverEmail(
              groupInfo,
              eventData,
              successMessages,
              errorMessages,
              member
            );
          } catch (error) {
            console.error(
              `Failed to send email for Registration ID: ${groupInfo.registrationID}`,
              error
            );
            errorMessages.push(
              `An error occurred for Registration ID: ${
                groupInfo.registrationID || "NA" + "\n"
              }`
            );
          }
        }
      }
    }
    // After processing all groups, show the modal with consolidated messages
    if (successMessages.length > 0 || errorMessages.length > 0) {
      const combinedMessages = [...successMessages, ...errorMessages];
      if (successMessages.length > 0) {
        setSuccessMessage(combinedMessages);
        setShowSuccessModal(true);
      } else {
        setError(combinedMessages);
        setShowErrorModal(true);
      }
    }
    setLoading(false);
  };

  // Bulk Emails
  const sendBulkEmail = async () => {
    console.log("sendEmail filters.retreatID:", filters.retreatID);
    // Check if retreatID is null
    if (!filters.retreatID) {
      setError("Error: Retreat ID is missing.");
      setShowErrorModal(true);
      return; // Exit the function if retreatID is null
    }
    if (!groups || groups.length === 0) {
      setError("Error: No groups found for the selected Retreat ID For Email.");
      setShowErrorModal(true);
      return; // Exit the function if there are no groups
    }
    // Arrays to store success and error messages
    const successMessages = [];
    const errorMessages = [];
    setLoading(true);

    for (const groupInfo of groups) {
      try {
        if (groupInfo.status === "Approved") {
          const eventData = await EventSupport.fetchEventData(
            groupInfo.retreatID
          );
          const emailData = {
            fullName: `${groupInfo.firstName} ${groupInfo.lastName}`,
            email: groupInfo.email,
            phone: groupInfo.phone,
            amount: groupInfo.amount,
            paymentMethod: groupInfo.paymentMethod,
            status: groupInfo.status,
            groupMembers: `${UtilMethods.formatGroupMembers(
              groupInfo.groupMembers
            )}`,
            registrationID: groupInfo.registrationID,
            retreatName: `${eventData.name}`,
            paidAmount: eventData.paidAmount,
            whatsAppLink: eventData.whatsAppLink,
            retreatEmail: eventData.retreatEmail,
          };
          console.log("Email Data:", emailData);
          const emailResponse = await sendEmail(
            emailData,
            "retreatApproval",
            "service_retreat"
          );
          if (emailResponse.success) {
            successMessages.push(
              `Registration ID: ${
                groupInfo.registrationID || "NA"
              }, Email confirmation sent to: ${groupInfo.email}` + "\n"
            );
          } else {
            errorMessages.push(
              `Failed to send email. Registration ID: ${
                groupInfo.registrationID || "NA"
              }, Email: ${groupInfo.email}` + "\n"
            );
          }
        } else {
          errorMessages.push(
            `Group not approved. Registration ID: ${
              groupInfo.registrationID || "NA" + "\n"
            }`
          );
        }
      } catch (error) {
        console.error(
          `Failed to send email for Registration ID: ${groupInfo.registrationID}`,
          error
        );
        errorMessages.push(
          `An error occurred for Registration ID: ${
            groupInfo.registrationID || "NA" + "\n"
          }`
        );
      }
    }
    // After processing all groups, show the modal with consolidated messages
    if (successMessages.length > 0 || errorMessages.length > 0) {
      const combinedMessages = [...successMessages, ...errorMessages];
      if (successMessages.length > 0) {
        setSuccessMessage(combinedMessages);
        setShowSuccessModal(true);
      } else {
        setError(combinedMessages);
        setShowErrorModal(true);
      }
    }
    setLoading(false);
  };

  const handleCancelRegClick = async (group) => {
    if (group.status === "To Be Cancelled") {
      if (group.paidAmount > 0) {
        setError(
          "Issue refund before confirming cancellation. Paid Amount : " +
            group.paidAmount +
            " | Refund Amount : " +
            group.refundAmount
        );
        setShowErrorModal(true);
        return;
      }
    }
    if (window.confirm("Are you sure you want to Cancel this Registration?")) {
      group.updatedBy = user?.userID;
      console.log("cancelRegistration group data:", group);

      try {
        const response = await EventSupport.updateGroupStatus(
          group,
          "Cancelled"
        );
        const eventData = await EventSupport.fetchEventData(group.retreatID);
        if (response) {
          if (response?.group?.status === "Cancelled") {
            const emailData = {
              fullName: group.firstName + " " + group.lastName,
              email: group.email,
              phone: group.phone,
              amount: group.amount,
              paymentMethod: group.paymentMethod,
              status: response?.group?.status || "Cancelled",
              groupMembers: UtilMethods.formatGroupMembers(group.groupMembers),
              registrationID: response?.group?.registrationID || "NA",
              eventData: eventData,
              retreatName: `${eventData.name} `,
              retreatEmail: eventData.retreatEmail,
            };
            const emailResponse = await sendEmail(
              emailData,
              "retreatCancel",
              "service_retreat"
            );
            if (emailResponse.success) {
              setSuccessMessage(
                response.message +
                  " Registration ID : " +
                  response.registrationID +
                  ", And Email confirmation sent to " +
                  group.email
              );
            }
          }
          setSuccessMessage(
            response.message + " Registration ID : " + response.registrationID
          );
          fetchGroups();
        } else {
          setError("Error during cancellation.");
        }
      } catch (error) {
        console.error("Error during registration:", error);
        setError(
          "Error during registration: " +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setError(null);
  };

  if (loading) {
    return (
      <React.Fragment>
        <div className="container-fluid position-relative p-0">
          <MenuBar />
        </div>
        <div className="container-fluid p-0">
          <div
            className="banner-image_sm"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
            }}
          >
            <div className="banner-heading text-center wow fadeInDown">
              <h1 className="text-white">Retreat Registrations</h1>
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
        <FooterPanel />
      </React.Fragment>
    );
  }

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <React.Fragment>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>
      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">Retreat Registrations</h1>
          </div>
        </div>
      </div>

      <div className="container-fluid mt-2 mb-1">
        {/* Mobile-friendly inline help shown when a field is focused/clicked */}
        <style>
          {`
            .tooltip-wrap { position: relative; display: inline-block; width:100%; }
            .tooltip-text {
              display: none;
              position: absolute;
              bottom: calc(100% + 8px);
              left: 50%;
              transform: translateX(-50%);
              background: rgba(0,0,0,0.85);
              color: #fff;
              padding:8px 10px;
              border-radius:6px;
              font-size:12px;
              white-space:nowrap;
              z-index:2000;
              max-width:260px;
              text-align:center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            }
            .tooltip-wrap:focus-within .tooltip-text { display:block; }
            .tooltip-text::after {
              content: '';
              position: absolute;
              top: 100%;
              left: 50%;
              transform: translateX(-50%);
              border-width: 6px;
              border-style: solid;
              border-color: rgba(0,0,0,0.85) transparent transparent transparent;
            }
          `}
        </style>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Name */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <input
                type="text"
                className="form-control"
                placeholder="Name"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                aria-describedby="help-name"
              />
              <div className="tooltip-text" id="help-name" role="status">
                Enter full or partial first/last name to filter registrations.
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <input
                type="text"
                className="form-control"
                placeholder="Email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                required
                aria-describedby="help-email"
              />
              <div className="tooltip-text" id="help-email" role="status">
                Type an email or part of an email (case-insensitive) to search.
              </div>
            </div>
          </div>

          {/* Phone */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <PhoneInput
                defaultCountry="US"
                flagUrl="https://flagcdn.com/w320/{xx}.png"
                placeholder="Phone"
                name="phone"
                value={filters.phone}
                onChange={(value) => setFilters({ ...filters, phone: value })}
                international
                aria-describedby="help-phone"
              />
              <div className="tooltip-text" id="help-phone" role="status">
                Enter phone.
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <select
                className="form-control"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                aria-describedby="help-status"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.key} value={status.key}>
                    {status.value}
                  </option>
                ))}
              </select>
              <div className="tooltip-text" id="help-status" role="status">
                Choose a status.
              </div>
            </div>
          </div>

          {/* Registration IDs */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <input
                type="text"
                className="form-control"
                placeholder="Registration IDs (comma separated)"
                name="registrationIDs"
                value={filters.registrationIDs}
                onChange={handleFilterChange}
                aria-describedby="help-regs"
              />
              <div className="tooltip-text" id="help-regs" role="status">
                Provide comma-separated registration IDs to search for specific
                records.
              </div>
            </div>
          </div>

          {/* Refund Amount */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <input
                type="text"
                className="form-control"
                placeholder="Refund Amount >"
                title="Refund Amount greater than entered value"
                name="refundAmount"
                value={filters.refundAmount}
                onChange={handleFilterChange}
                aria-describedby="help-refund"
              />
              <div className="tooltip-text" id="help-refund" role="status">
                Show registrations with refund amount greater than the entered
                value.
              </div>
            </div>
          </div>

          {/* Retreat */}
          <div className="flex-grow-1">
            <div className="form-group mb-0 tooltip-wrap">
              <select
                className="form-control"
                name="retreatID"
                value={filters.retreatID}
                onChange={handleFilterChange}
                required
                aria-describedby="help-retreat"
              >
                <option value="">Select Retreat</option>
                {events
                  .sort((a, b) => b.id - a.id)
                  .map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.id} : {event.name}
                    </option>
                  ))}
              </select>
              <div className="tooltip-text" id="help-retreat" role="status">
                Select a retreat.
              </div>
            </div>
          </div>

          {/* List Button */}
          <div>
            <button
              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
              onClick={fetchGroups}
              title="View the list of groups"
            >
              <i className="fas fa-list"></i>
              List
            </button>
          </div>

          {/* Clear Button */}
          <div>
            <button
              className="btn btn-secondary btn-sm d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                setFilters({
                  email: "",
                  phone: "",
                  retreatID: "",
                  registrationIDs: "",
                  status: "",
                  refundAmount: "",
                  name: "",
                });
                const activeRetreat = events.find((event) =>
                  UtilMethods.canRegister(
                    event.reg_date,
                    event.reg_end_date ? event.reg_end_date : event.start_date
                  )
                );
                if (activeRetreat) {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    retreatID: activeRetreat.id.toString(),
                  }));
                }
                setGroups([]);
              }}
              title="Clear all filters"
            >
              <i className="fas fa-eraser"></i>
              Clear
            </button>
          </div>
        </div>

        <div
          className="row mt-2 justify-content-center"
          style={{
            border: "1px solid rgb(145, 86, 19)",
            borderRadius: "5px",
            padding: "2px",
          }}
        >
          {/* Admin Actions Label */}
          <div className="col-12 text-center mb-0 mt-0">
            <strong>Admin Actions</strong>
          </div>

          <div className="d-flex flex-wrap gap-2 justify-content-center mb-1">
            {/* Register New Button */}
            <div className="form-group text-center">
              <button
                className="btn btn-secondary btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={registerNewClick}
                title="Register a new user or entity"
              >
                <i className="fas fa-user-plus"></i>
                Register New
              </button>
            </div>

            {/* Upload Button */}
            <div className="form-group text-center">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileSelection}
                accept=".xlsx, .xls"
              />
              <button
                className="btn btn-secondary btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={triggerFileUpload}
                title="Upload an Excel file"
              >
                <i className="fas fa-upload"></i>
                Upload & Confirm
              </button>
            </div>

            {/* Download Template Button */}
            <div className="form-group text-center">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={downloadTemplate}
                title="Download the Excel template"
              >
                <i className="fas fa-download"></i>
                Download Template
              </button>
            </div>
            {/* Download Payments Button 
            <div className="form-group text-center">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={async () => {
                  if (!filters.retreatID) {
                    setError(
                      "Please select a Retreat ID before downloading payments."
                    );
                    setShowErrorModal(true);
                    return;
                  }
                  let data;
                  try {
                    const eventData = await EventSupport.fetchEventData(
                      filters.retreatID
                    );
                    const start_date = eventData.reg_date;
                    const end_date = eventData.reg_end_date ? eventData.reg_end_date : eventData.start_date;
                    const response = await getAllTransactions(
                      start_date,
                      end_date
                    );
                    console.log("Fetched transactions:", response);
                    // The response is from PayPal v1/reporting/transactions
                    // Extract the transactions array from the response
                    data =
                      response?.transaction_details?.map((txn) => {
                        const payerInfo = txn?.payer_info || {};
                        const transactionInfo = txn?.transaction_info || {};
                        return {
                          registrationID: transactionInfo?.invoice_id || "",
                          firstName: payerInfo?.first_name || "",
                          lastName: payerInfo?.last_name || "",
                          email: payerInfo?.email_address || "",
                          phone: payerInfo?.phone_number || "",
                          amount:
                            transactionInfo?.transaction_amount?.value || "",
                          paymentMethod:
                            transactionInfo?.payment_method_type || "",
                          paymentInfo: transactionInfo?.transaction_id || "",
                          paidAmount:
                            transactionInfo?.transaction_amount?.value || "",
                          refundAmount:
                            transactionInfo?.fee_amount?.value || "",
                          status: transactionInfo?.transaction_status || "",
                          specialRequest: "",
                        };
                      }) || [];
                  } catch (err) {
                    setError("Failed to fetch groups: " + err.message);
                    setShowErrorModal(true);
                    return;
                  }
                  const paymentData = [
                    [
                      "Registration ID",
                      "Name",
                      "Email",
                      "Phone",
                      "Amount",
                      "Payment Method",
                      "Payment Info",
                      "Paid Amount",
                      "Refund Amount",
                      "Status",
                      "Special Request",
                    ],
                  ];
                  data.forEach((group) => {
                    paymentData.push([
                      group.registrationID,
                      group.firstName + " " + group.lastName,
                      group.email,
                      group.phone,
                      group.amount,
                      group.paymentMethod || "",
                      group.paymentInfo || "",
                      group.paidAmount,
                      group.refundAmount,
                      group.status,
                      group.specialRequest || "",
                    ]);
                  });
                  const worksheet = XLSX.utils.aoa_to_sheet(paymentData);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
                  const binaryWorkbook = XLSX.write(workbook, {
                    bookType: "xlsx",
                    type: "binary",
                  });
                  const blob = new Blob(
                    [
                      new Uint8Array(
                        binaryWorkbook
                          .split("")
                          .map((char) => char.charCodeAt(0))
                      ),
                    ],
                    { type: "application/octet-stream" }
                  );
                  saveAs(blob, "RetreatPayments.xlsx");
                }}
                title="Download payments as Excel"
              >
                <i className="fas fa-file-excel"></i>
                Download Payments
              </button>
            </div>*/}
            {/* Send Email Button */}
            <div className="form-group text-center">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={sendBulkEmail}
                title="Send an email to Approved Registrations"
              >
                <i className="fas fa-envelope"></i>
                Send Email
              </button>
            </div>
            <div className="form-group text-center">
              <div className="dropdown">
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2 dropdown-toggle"
                  type="button"
                  id="sendWaiverDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fas fa-envelope"></i>
                  Send Waiver Form
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="sendWaiverDropdown"
                >
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={async () => {
                        await sendWaiverFormEmail(null, null, "GROUPS");
                      }}
                    >
                      Group Level
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={async () => {
                        await sendWaiverFormEmail(null, null, "GROUPS-MEMBERS");
                      }}
                    >
                      Member Level
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div
          className="row mt-0.5 mb-0.5 justify-content-center"
          style={{
            border: "1px solid rgb(145, 86, 19)", // Blue border (change color as needed)
            borderRadius: "5px", // Optional: rounded corners
            padding: "2px", // Optional: space inside the border
          }}
        >
          <div className="col-12 text-center mb-1">
            <strong onClick={toggleSummary} style={{ cursor: "pointer" }}>
              Summary {isSummaryExpanded ? "▲" : "▼"}
            </strong>
          </div>
          {isSummaryExpanded && (
            <div className="row mb-1 justify-content-center">
              <div className="col-12">
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <div className="summary-item">
                    <h5>Registrations by Status</h5>
                    <p className="mb-0">
                      {"Total : "}
                      {groups.length}
                    </p>
                    {Object.entries(
                      groups.reduce((acc, group) => {
                        acc[group.status] = (acc[group.status] || 0) + 1;
                        return acc;
                      }, {})
                    )
                      .map(([status, count]) => `${status}: ${count}`)
                      .join(", ")}
                  </div>
                  <div className="summary-item">
                    <h5>Participants by Status</h5>
                    <p className="mb-0">
                      Total(Non Cancelled) :{" "}
                      {groups.reduce(
                        (acc, group) =>
                          acc +
                          group.groupMembers.filter((member) => !member.deleted)
                            .length,
                        0
                      )}
                    </p>
                    <p className="mb-0">
                      {Object.entries(
                        groups.reduce((acc, group) => {
                          const nonDeletedMembers =
                            group.groupMembers?.filter(
                              (member) => !member.deleted
                            ) || [];

                          if (nonDeletedMembers.length > 0) {
                            acc[group.status] =
                              (acc[group.status] || 0) +
                              nonDeletedMembers.length;
                          }

                          return acc;
                        }, {})
                      ).map(([status, count]) => {
                        // Assign a color based on status
                        let color = "#343a40";
                        switch (status) {
                          case "New":
                            color = "#ffc107";
                            break;
                          case "Draft":
                            color = "#fd7e14";
                            break;
                          case "Approved":
                          case "Confirmed":
                          case "Paid":
                            color = "#28a745";
                            break;
                          case "Cancelled":
                          case "To Be Cancelled":
                            color = "#dc3545";
                            break;
                          default:
                            color = "#343a40";
                        }
                        return (
                          <span
                            key={status}
                            style={{
                              color,
                              fontWeight: "bold",
                              marginRight: 8,
                            }}
                          >
                            {status}: {count}
                          </span>
                        );
                      })}{" "}
                      {"Cancelled : "}
                      <span style={{ color: "#dc3545", fontWeight: "bold" }}>
                        {groups.reduce(
                          (acc, group) =>
                            acc +
                            group.groupMembers.filter(
                              (member) => member.deleted
                            ).length,
                          0
                        )}
                      </span>{" "}
                    </p>
                  </div>
                  <div className="summary-item">
                    <h5>Total Amount</h5>
                    <p className="mb-0">
                      {groups.reduce((acc, group) => acc + group.amount, 0)}
                    </p>
                  </div>
                  <div className="summary-item">
                    <h5>Paid Amount</h5>
                    <p className="mb-0">
                      {groups.reduce((acc, group) => acc + group.paidAmount, 0)}
                    </p>
                  </div>
                  <div className="summary-item">
                    <h5>Refund Amount</h5>
                    <p className="mb-0">
                      {groups.reduce(
                        (acc, group) => acc + group.refundAmount,
                        0
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <ul className="list-group mt-1 mb-1">
          {currentRecords.map((group) => (
            <li className="list-group-item" key={group.registrationID}>
              <h5 className="mb-1 mt-1">
                Retreat ID: {group.retreatID} | Registration ID:{" "}
                {group.registrationID} | {group.firstName} {group.lastName}{" "}
                (Email: {group.email} | Phone: {group.phone})
              </h5>
              <p className="mb-1 mt-1">
                Status:{" "}
                <span
                  style={{
                    color:
                      group.status === "New"
                        ? "#ffc107"
                        : group.status === "Draft"
                        ? "#fd7e14"
                        : group.status === "Approved" ||
                          group.status === "Paid" ||
                          group.status === "Confirmed"
                        ? "#28a745"
                        : group.status === "Cancelled" ||
                          group.status === "To Be Cancelled"
                        ? "#dc3545"
                        : "#343a40",
                    fontWeight: "bold",
                  }}
                >
                  {group.status}
                </span>
                {" | "}
                Count: {group.numberOfParticipants} {" | "}
                Amount: {group.amount}
                {" | "}
                <span style={{ color: "green" }}>
                  Paid Amount: {group.paidAmount} |{" "}
                </span>
                <span style={{ color: "red" }}>
                  Refund Amount: {group.refundAmount}
                </span>
              </p>
              <div className="d-flex flex-wrap align-items-center gap-1 mb-1 mt-0">
                {/* Edit Button */}
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1 mb-1 mt-0"
                  onClick={() => handleEditClick(group)}
                  aria-label="Edit group"
                >
                  <i className="fas fa-edit"></i>
                  Edit
                </button>

                {/* Separator */}
                <span className="text-muted d-none d-sm-inline">|</span>

                {/* Cancel Registration Button */}
                <button
                  className="btn btn-danger btn-sm d-flex align-items-center gap-1  mb-1 mt-0"
                  onClick={() => handleCancelRegClick(group)}
                  disabled={group.status === "Cancelled"}
                  aria-label="Cancel Registration"
                >
                  <i className="fas fa-times-circle"></i>
                  {group.status === "To Be Cancelled"
                    ? "Confirm Cancel"
                    : "Cancel Reg"}
                </button>

                {/* Separator */}
                <span className="text-muted d-none d-sm-inline">|</span>

                {/* Approve or Status Display */}
                {group.status === "New" || group.status === "Paid" ? (
                  <button
                    className="btn btn-success btn-sm d-flex align-items-center gap-1  mb-1 mt-0"
                    onClick={() => handleEditClick(group)}
                    aria-label="Confirm Registration"
                  >
                    <i className="fas fa-check-circle"></i>
                    Confirm
                  </button>
                ) : (
                  <span
                    className={`badge d-flex align-items-center gap-1 mb-1 mt-0`}
                    style={{
                      textAlign: "center",
                      border: `2px solid ${
                        group.status === "New"
                          ? "#ffc107"
                          : group.status === "Draft"
                          ? "#fd7e14"
                          : group.status === "Approved" ||
                            group.status === "Paid" ||
                            group.status === "Confirmed"
                          ? "#28a745"
                          : group.status === "Cancelled" ||
                            group.status === "To Be Cancelled"
                          ? "#dc3545"
                          : "#6c757d"
                      }`,
                      borderRadius: "10px",
                      padding: "0.25rem 0.5rem",
                      minWidth: "80px",
                      backgroundColor:
                        group.status === "New"
                          ? "#fff3cd"
                          : group.status === "Draft"
                          ? "#ffe5d0"
                          : group.status === "Approved" ||
                            group.status === "Paid" ||
                            group.status === "Confirmed"
                          ? "#d4edda"
                          : group.status === "Cancelled" ||
                            group.status === "To Be Cancelled"
                          ? "#f8d7da"
                          : "#f8f9fa",
                      color:
                        group.status === "New"
                          ? "#856404"
                          : group.status === "Draft"
                          ? "#a35d00"
                          : group.status === "Approved" ||
                            group.status === "Paid" ||
                            group.status === "Confirmed"
                          ? "#155724"
                          : group.status === "Cancelled" ||
                            group.status === "To Be Cancelled"
                          ? "#721c24"
                          : "#343a40",
                    }}
                    aria-label={`Status: ${group.status}`}
                  >
                    <i className="fas fa-info-circle"></i>
                    {group.status}
                  </span>
                )}
                {" || "}
                <div className="dropdown">
                  <button
                    className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2 dropdown-toggle"
                    type="button"
                    id="sendWaiverDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="fas fa-envelope"></i>
                    Send Waiver Form
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="sendWaiverDropdown"
                  >
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          sendWaiverFormEmail(group, null, "GROUP")
                        }
                      >
                        Group Level
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={async () => {
                          await Promise.all([
                            sendWaiverFormEmail(group, null, "MEMBER"),
                          ]);
                        }}
                      >
                        Member Level
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Group Members */}
              <ul className="list-group mt-0">
                {group.groupMembers.map((participant) => (
                  <li
                    className="list-group-item mb-0 mt-0 p-2"
                    key={participant.memberID}
                  >
                    <div className="row text-center text-md-start">
                      <div className="col-md-1 fw-bold">
                        {participant?.memberID || "N/A"}{" "}
                        {participant?.deleted ? (
                          <i
                            className="fas fa-times-circle text-danger"
                            title="Cancelled"
                          ></i>
                        ) : (
                          <i
                            className="fas fa-check-circle text-success"
                            title="Active"
                          ></i>
                        )}
                      </div>
                      <div className="col-md-2" title="Participant Name">
                        {`${participant?.firstName || " "} ${
                          participant?.lastName || ""
                        }`}
                      </div>
                      <div className="col-md-3" title="Participant Email">
                        {participant?.email || "N/A"}
                      </div>
                      <div className="col-md-1" title="Age">
                        {participant?.age || "N/A"}
                      </div>
                      <div className="col-md-2" title="Gender">
                        {participant?.gender || "N/A"}
                      </div>
                      <div className="col-md-1" title="Relationship">
                        {participant?.relation || "N/A"}
                      </div>
                      <div className="col-md-1">
                        {participant?.waiverFilePath ? (
                          <i
                            className="fas fa-user-check text-success"
                            title="Wiver Signed"
                          ></i>
                        ) : (
                          <i
                            className="fas fa-user-xmark text-danger"
                            title="Wiver Not signed"
                          ></i>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        {/* Pagination  */}
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination">
            {Array.from({ length: totalPages }, (_, index) => (
              <li
                key={index + 1}
                className={`page-item ${
                  currentPage === index + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link btn-sm"
                  onClick={() => handlePageChange(index + 1)}
                  aria-current={currentPage === index + 1 ? "page" : undefined}
                >
                  {index + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Success Modal */}
        <AlertModal
          show={showSuccessModal}
          title="Successful"
          message={successMessage}
          type="success"
          details={{}} // or pass additional details if necessary
          onCancel={handleCloseSuccessModal}
        />
        {/* Error Modal */}

        <AlertModal
          show={showErrorModal}
          title="Error in Retreat Registration"
          type="error"
          message={error}
          details={{}}
          onCancel={handleCloseErrorModal}
        />
      </div>
      <FooterPanel />
    </React.Fragment>
  );
};

export default AdminRetreatRegistrationPage;
